import type { Campaign, CampaignOccurrence } from '@clarity/contract';
import { num, pct } from '../format.js';

/**
 * Shared occurrence helpers (clarity-campaign-matching task 13.4) — the pieces
 * the occurrences LIST (in {@link CampaignDetail}) and the {@link OccurrenceDetail}
 * both render: the date-window label, the resolved-vs-default override diff, and
 * the confidence meter. Kept in one place so the list chip and the detail table
 * can't drift. Pure + presentational; no query or domain logic.
 */

/** Confidence levels the matcher grades (Req 6.2). */
export type Confidence = 'high' | 'medium' | 'low';

/** The discount terms a campaign/occurrence can carry (point OR declared range). */
interface DiscountTermsLike {
  discountPct: number | null;
  discountPctMin: number | null;
  discountPctMax: number | null;
}

/**
 * Format a discount term for display (clarity-campaign-matching, discount-range
 * feature). A DECLARED RANGE (`discountPctMin`/`discountPctMax`, max > min)
 * renders as "15–50%"; a single `discountPct` renders as "50%"; nothing stated
 * ⇒ `null` (the caller omits the term). One place so the list, the detail
 * header, and the occurrence compare table can't drift.
 */
export function discountRangeLabel(terms: DiscountTermsLike, locale?: string): string | null {
  const { discountPctMin: min, discountPctMax: max, discountPct } = terms;
  if (min !== null && max !== null && max > min) {
    // "15–50%" — one trailing % (pct() already appends it to the upper bound).
    const lo = Math.round(min * 100).toLocaleString(locale);
    return `${lo}–${pct(Math.round(max * 100), locale)}`;
  }
  if (discountPct !== null) return pct(Math.round(discountPct * 100), locale);
  return null;
}

const CONF_LABEL: Record<Confidence, string> = {
  high: 'High',
  medium: 'Medium',
  low: 'Low',
};

const CONF_FILLED: Record<Confidence, number> = { high: 3, medium: 2, low: 1 };

/**
 * The confidence meter (Req 6.5) — three bars + the level word, reproduced from
 * the mock's `.conf` component. The level is carried by TEXT (the word), not the
 * bar colour alone, so it reads without colour (WCAG 1.4.1); the bars are
 * decorative (`aria-hidden`).
 */
export function ConfidenceMeter({
  level,
  withLabel = true,
}: {
  level: Confidence;
  /** Show the level word next to the bars (the list chip hides it to stay compact). */
  withLabel?: boolean;
}) {
  const filled = CONF_FILLED[level];
  const label = CONF_LABEL[level];
  return (
    <span className={`conf conf--${level}`}>
      <span className="conf__bars" aria-hidden="true">
        {[0, 1, 2].map((i) => (
          <i key={i} className={i < filled ? 'is-on' : ''} />
        ))}
      </span>
      {withLabel ? label : <span className="sr-only">{`${label} confidence`}</span>}
    </span>
  );
}

/** A date window label: the inclusive day range (e.g. "Sep 8–9") + the month tag. */
export interface OccurrenceWindow {
  /** The inclusive day range, same-month collapsed ("Sep 8–9", else "Sep 30 – Oct 1"). */
  range: string;
  /** The month + year tag ("Sep 2026"). */
  month: string;
}

/**
 * Format an occurrence's `[startDate, endDate]` as the mock's two-line window.
 * The occurrence window is INCLUSIVE of both bounds (unlike the half-open
 * reporting period), so both days render as-is. Pinned to UTC so the rendered
 * day matches the record's UTC boundary regardless of the viewer's zone.
 */
export function occurrenceWindow(occurrence: CampaignOccurrence, locale?: string): OccurrenceWindow {
  const from = new Date(occurrence.startDate);
  const to = new Date(occurrence.endDate);
  if (Number.isNaN(from.getTime()) || Number.isNaN(to.getTime())) {
    return { range: `${occurrence.startDate} – ${occurrence.endDate}`, month: '' };
  }
  const monthShort = (d: Date) => d.toLocaleDateString(locale, { month: 'short', timeZone: 'UTC' });
  const day = (d: Date) => d.toLocaleDateString(locale, { day: 'numeric', timeZone: 'UTC' });
  const monthYear = (d: Date) =>
    d.toLocaleDateString(locale, { month: 'short', year: 'numeric', timeZone: 'UTC' });

  // Same month ⇒ name the month once ("Sep 8–9"); the dash is a real en-dash.
  const sameMonth =
    from.getUTCFullYear() === to.getUTCFullYear() && from.getUTCMonth() === to.getUTCMonth();
  const range = sameMonth
    ? `${monthShort(from)} ${day(from)}–${day(to)}`
    : `${monthShort(from)} ${day(from)} – ${monthShort(to)} ${day(to)}`;
  return { range, month: monthYear(from) };
}

/**
 * The compact override chips the LIST row shows (the mock's `over-chip`s): one
 * per field the occurrence actually overrides, labelled with the overridden
 * value (e.g. "cap 3 JOD", "disc 30%"). Returns `[]` when nothing is
 * overridden, so the list can show its "no overrides" placeholder.
 */
export function occurrenceChips(
  occurrence: CampaignOccurrence,
  campaign: Campaign,
  currency = 'JOD',
  locale?: string,
): string[] {
  const chips: string[] = [];
  const discChanged =
    (occurrence.discountPct !== null && occurrence.discountPct !== campaign.discountPct) ||
    (occurrence.discountPctMin !== null && occurrence.discountPctMin !== campaign.discountPctMin) ||
    (occurrence.discountPctMax !== null && occurrence.discountPctMax !== campaign.discountPctMax);
  if (discChanged) {
    const label = discountRangeLabel(
      {
        discountPct: occurrence.discountPct ?? campaign.discountPct,
        discountPctMin: occurrence.discountPctMin ?? campaign.discountPctMin,
        discountPctMax: occurrence.discountPctMax ?? campaign.discountPctMax,
      },
      locale,
    );
    if (label !== null) chips.push(`disc ${label}`);
  }
  if (occurrence.maxDiscountCap !== null && occurrence.maxDiscountCap !== campaign.maxDiscountCap) {
    chips.push(`cap ${num(occurrence.maxDiscountCap, locale)} ${currency}`);
  }
  if (occurrence.coFundPct !== null && occurrence.coFundPct !== campaign.coFundPct) {
    chips.push(`co-fund ${pct(Math.round(occurrence.coFundPct * 100), locale)}`);
  }
  if (occurrence.audience !== null && occurrence.audience !== campaign.audience) {
    const a = occurrence.audience;
    chips.push(a === 'all' ? 'all customers' : a === 'plus' ? 'Plus only' : 'non-Plus only');
  }
  if (occurrence.minOrder !== null && occurrence.minOrder !== campaign.minOrder) {
    chips.push(`min ${num(occurrence.minOrder, locale)} ${currency}`);
  }
  return chips;
}

/** One row of the occurrence override diff — a field, its campaign default and
 *  this occurrence's resolved value, and whether the occurrence overrides it. */
export interface OverrideRow {
  /** The field name shown in the "Field" column. */
  label: string;
  /** The campaign-default rendering ("—" when the campaign carries none). */
  campaignValue: string;
  /** This occurrence's resolved rendering. */
  occurrenceValue: string;
  /** True when the occurrence sets a NON-NULL override that differs from the default. */
  changed: boolean;
}

/**
 * Resolve the occurrence's override table (Req 9.4) — one row per term, with the
 * campaign default beside this occurrence's resolved value. A field is `changed`
 * only when the occurrence sets a non-null override; a `null` occurrence field
 * inherits the parent (never highlighted). The dates row is always "—" on the
 * default side (an occurrence always carries its own window).
 */
export function occurrenceOverrides(
  occurrence: CampaignOccurrence,
  campaign: Campaign,
  currency = 'JOD',
  locale?: string,
): OverrideRow[] {
  const money = (v: number | null) => (v === null ? 'none' : `${num(v, locale)} ${currency}`);
  const audienceLabel = (a: Campaign['audience'] | null) =>
    a === null ? '—' : a === 'all' ? 'All' : a === 'plus' ? 'Plus (TPRO)' : 'Non-Plus';

  const window = occurrenceWindow(occurrence, locale);

  // A term is overridden when the occurrence sets it non-null AND it differs
  // from the campaign default.
  const diff = <T,>(occ: T | null, def: T): boolean => occ !== null && occ !== def;

  return [
    {
      label: 'Dates',
      campaignValue: '—',
      occurrenceValue: window.range,
      changed: false,
    },
    {
      // A ranged offer shows "15–50%" (via discountRangeLabel); a point offer
      // shows the single "%" — the label adapts and the row header follows.
      label: campaign.discountPctMax !== null || occurrence.discountPctMax !== null
        ? 'Discount range'
        : 'Discount',
      campaignValue: discountRangeLabel(campaign, locale) ?? '—',
      occurrenceValue:
        discountRangeLabel(
          {
            discountPct: occurrence.discountPct ?? campaign.discountPct,
            discountPctMin: occurrence.discountPctMin ?? campaign.discountPctMin,
            discountPctMax: occurrence.discountPctMax ?? campaign.discountPctMax,
          },
          locale,
        ) ?? '—',
      changed:
        diff(occurrence.discountPct, campaign.discountPct) ||
        diff(occurrence.discountPctMin, campaign.discountPctMin) ||
        diff(occurrence.discountPctMax, campaign.discountPctMax),
    },
    {
      label: 'Max discount cap',
      campaignValue: money(campaign.maxDiscountCap),
      occurrenceValue: money(occurrence.maxDiscountCap ?? campaign.maxDiscountCap),
      changed: diff(occurrence.maxDiscountCap, campaign.maxDiscountCap),
    },
    {
      label: 'Co-funding %',
      campaignValue: pct(Math.round(campaign.coFundPct * 100), locale),
      occurrenceValue: pct(Math.round((occurrence.coFundPct ?? campaign.coFundPct) * 100), locale),
      changed: diff(occurrence.coFundPct, campaign.coFundPct),
    },
    {
      label: 'Audience',
      campaignValue: audienceLabel(campaign.audience),
      occurrenceValue: audienceLabel(occurrence.audience ?? campaign.audience),
      changed: diff(occurrence.audience, campaign.audience),
    },
    {
      label: 'Min order',
      campaignValue: money(campaign.minOrder),
      occurrenceValue: money(occurrence.minOrder ?? campaign.minOrder),
      changed: diff(occurrence.minOrder, campaign.minOrder),
    },
  ];
}
